import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import {
  applyTypography,
  BaseButton,
  useAuth,
  LoginRequiredModal,
  trackCtaClick,
  trackWorkshopBookComplete,
} from '@repo/common';
import DateSlotCard from './PathFeed/DateSlotCard';
import BookingConflictModal from './Modal/BookingConflictModal';
import SuccessNotificationToast from './Modal/SuccessNotificationModal';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';
import { addToCart, getCartItems } from '../../api/cart/cart.api';
import { createBookings, getUserBookings } from '../../api/booking/booking.api';
import IntakeQuestionsModal from './Modal/IntakeQuestionsModal';
import useIntakeGate from './hooks/useIntakeGate';
import { buildAnswersPayload, clearIntakeAnswers } from './utils/intakeAnswers';
import { getWorkshopDetail } from '../../api/workshop/workshop.api';

const DRAG_THRESHOLD = 50;
// 이 값 이상 아래로 움직여야 '드래그(닫기)'로 간주한다.
// 그 미만은 '탭'으로 보고 preventDefault를 걸지 않아, 모바일에서 클릭이 삼켜지지 않게 한다.
const DRAG_ACTIVATE_THRESHOLD = 10;
const CLOSE_ANIMATION_DURATION = 300;

const DateSelectionModal = ({
  isOpen,
  onClose,
  dateSlots = [],
  hasExistingBooking = false,
  onDateSelected,
  buttonText,
  isBlocked,
  /** 사전 질문 게이트용. 대기열도 결국 같은 예약 행이 되므로 여기서 받아둔다 */
  workshop,
}) => {
  const intakeGate = useIntakeGate();
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [startY, setStartY] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('bag');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [slotStatusMap, setSlotStatusMap] = useState({});
  const [hasBookedWorkshop, setHasBookedWorkshop] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoggedIn, user } = useAuth();

  const touchMovedRef = useRef(false);
  const isTouchingRef = useRef(false);
  const modalRef = useRef(null);

  const startCloseAnimationThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      callback();
      setIsClosing(false);
    }, CLOSE_ANIMATION_DURATION);
  };

  const handleTouchStart = (e) => {
    if (window.innerWidth >= 768) return;
    isTouchingRef.current = true;
    touchMovedRef.current = false;
    setStartY(e.touches[0].clientY);
    // 실제로 임계값 이상 끌어내리기 전까지는 드래그로 간주하지 않는다.
    // (탭 중 발생하는 미세한 움직임에 preventDefault가 걸려 클릭이 삼켜지는 것을 방지)
  };

  const handleTouchMove = (e) => {
    if (!isTouchingRef.current) return;
    const diffY = e.touches[0].clientY - startY;
    // 임계값 이상 아래로 움직였을 때만 시트를 끌어내리고 기본 동작을 막는다.
    if (diffY > DRAG_ACTIVATE_THRESHOLD) {
      if (!isDragging) setIsDragging(true);
      touchMovedRef.current = true;
      setTranslateY(diffY);
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (!isTouchingRef.current) return;
    isTouchingRef.current = false;

    if (isDragging) {
      setIsDragging(false);
      if (translateY > DRAG_THRESHOLD) {
        startCloseAnimationThen(() => {
          onClose && onClose();
        });
      }
      setTranslateY(0);
    }

    touchMovedRef.current = false;
  };

  const handleOverlayClick = (e) => {
    // 모달은 portal로 body에 렌더되지만 React 이벤트는 부모(카드 article의 onClick)로
    // 버블되므로, 모달 영역 클릭이 상세페이지 이동으로 새지 않게 전파를 막는다.
    e.stopPropagation();

    // 배경(오버레이) 자체를 직접 눌렀을 때만 닫는다.
    // 시트 내부(날짜 카드/버튼/빈 영역)를 눌렀을 때는 닫히지 않게 한다.
    if (e.target !== e.currentTarget) return;
    if (isDragging || touchMovedRef.current || isClosing) return;

    startCloseAnimationThen(() => {
      onClose && onClose();
    });
  };

  const handleCancel = () => {
    startCloseAnimationThen(() => {
      onClose && onClose();
    });
  };

  const displayDateSlots = useMemo(
    () => (Array.isArray(dateSlots) ? dateSlots : []),
    [dateSlots],
  );

  useEffect(() => {
    if (!isLoggedIn || displayDateSlots.length === 0) {
      setSlotStatusMap({});
      setHasBookedWorkshop(false);
      return;
    }
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const syncSlotStatuses = async () => {
      try {
        const slotIds = new Set(
          displayDateSlots.map((slot) => String(slot.id)),
        );
        const [cartResult, bookingsResult] = await Promise.allSettled([
          getCartItems({ skipLoading: true }),
          getUserBookings({ skipLoading: true }),
        ]);
        const cartItems =
          cartResult.status === 'fulfilled' ? cartResult.value : [];
        const bookings =
          bookingsResult.status === 'fulfilled' ? bookingsResult.value : [];

        const nextMap = {};
        let nextHasBookedWorkshop = false;

        (cartItems || []).forEach((item) => {
          const rawSlotId =
            item?.workshopDateSlotId ?? item?.workshopDateSlot?.id;
          const slotId = String(rawSlotId);
          if (slotIds.has(slotId)) {
            nextMap[slotId] = 'inBag';
          }
        });

        (bookings || []).forEach((booking) => {
          const rawSlotId =
            booking?.workshopDateSlotId ??
            booking?.workshopDateSlot?.id ??
            booking?.dateSlotId;
          const slotId = String(rawSlotId);
          const status = String(
            booking?.status ?? booking?.bookingStatus ?? '',
          ).toUpperCase();
          if (!slotIds.has(slotId)) return;
          const isWaitlist =
            status.includes('WAITLIST') || status.includes('WAITING');
          const isPendingPayment =
            status.includes('HOLD') || status.includes('OFFERED');
          const isConfirmed = status.includes('CONFIRMED');
          const isCanceled =
            status.includes('CANCEL') ||
            status.includes('EXPIRED') ||
            status.includes('REFUND');
          if (isCanceled) return;
          if (isWaitlist) {
            nextMap[slotId] = 'onWaitlist';
          } else if (isPendingPayment) {
            nextMap[slotId] = 'pendingPayment';
          } else if (isConfirmed) {
            nextMap[slotId] = 'booked';
            nextHasBookedWorkshop = true;
          }
        });

        if (!cancelled) {
          setSlotStatusMap(nextMap);
          setHasBookedWorkshop(nextHasBookedWorkshop);
        }
      } catch (error) {
        console.error('Failed to sync slot statuses:', error);
      }
    };

    syncSlotStatuses();

    return () => {
      cancelled = true;
    };
  }, [displayDateSlots, isLoggedIn, isOpen]);

  const displayDateSlotsWithStatus = useMemo(() => {
    return displayDateSlots.map((slot) => ({
      ...slot,
      workshopStatus: slotStatusMap[String(slot.id)] || slot.workshopStatus,
    }));
  }, [displayDateSlots, slotStatusMap]);

  const getSelectedSlot = () => {
    return displayDateSlotsWithStatus.find(
      (slot) => String(slot.id) === String(selectedSlotId),
    );
  };

  const inferWorkshopStatus = (slot) => {
    if (!slot) return 'available';
    if (slot.workshopStatus) {
      const workshopStatus = String(slot.workshopStatus).toLowerCase();
      if (
        workshopStatus.includes('pending') ||
        workshopStatus.includes('hold') ||
        workshopStatus.includes('offered')
      ) {
        return 'inBag';
      }
      return slot.workshopStatus;
    }
    const dateSlotStatus = String(slot.dateSlotStatus || '').toLowerCase();
    if (dateSlotStatus === 'ongoing') return 'ongoing';
    if (dateSlotStatus === 'closed') return 'closed';
    if (dateSlotStatus === 'completed') return 'completed';
    const normalizedStatus = String(slot.status || '').toLowerCase();
    if (normalizedStatus.includes('bag')) return 'inBag';
    if (normalizedStatus.includes('booked')) return 'booked';
    if (
      normalizedStatus.includes('pending') ||
      normalizedStatus.includes('hold') ||
      normalizedStatus.includes('offered')
    ) {
      return 'inBag';
    }
    if (
      normalizedStatus.includes('waitlist') ||
      normalizedStatus.includes('wait list') ||
      normalizedStatus.includes('waiting')
    ) {
      return 'onWaitlist';
    }
    if (slot.available === false || slot.status === 'Full') return 'full';
    return 'available';
  };

  const getButtonState = () => {
    if (isBlocked) {
      return { type: 'blocked', text: 'Booking not available', disabled: true };
    }

    const selectedSlot = getSelectedSlot();
    if (!selectedSlot) return null;
    if (hasExistingBooking || hasBookedWorkshop) {
      return { type: 'booked', text: 'Booked', disabled: true };
    }
    const workshopStatus = inferWorkshopStatus(selectedSlot);

    // 선택된 슬롯의 상태에 따라 버튼 상태 결정
    if (workshopStatus === 'inBag') {
      return { type: 'inBag', text: 'In your bag', disabled: true };
    }
    if (workshopStatus === 'onWaitlist') {
      return { type: 'onWaitlist', text: 'On waitlist', disabled: true };
    }
    if (workshopStatus === 'booked') {
      return { type: 'booked', text: 'Booked', disabled: true };
    }
    if (
      workshopStatus === 'ongoing' ||
      workshopStatus === 'closed' ||
      workshopStatus === 'completed'
    ) {
      return { type: workshopStatus, text: 'Add to bag', disabled: true };
    }
    if (workshopStatus === 'full') {
      return { type: 'joinWaitlist', text: 'Join waitlist', disabled: false };
    }
    // available 상태
    return {
      type: 'addToBag',
      text: buttonText || 'Add to bag',
      disabled: false,
    };
  };

  const handleAddToBag = async () => {
    if (!selectedSlotId) return;

    const selectedSlot = getSelectedSlot();
    if (!selectedSlot) return;

    // 비로그인 상태 체크
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // 이미 다른 예약이나 대기자 명단이 있는 경우 경고 모달 표시
    if (
      hasExistingBooking &&
      inferWorkshopStatus(selectedSlot) === 'available'
    ) {
      setShowConflictModal(true);
      return;
    }

    try {
      // onDateSelected 콜백이 있으면 업데이트 모드, 없으면 추가 모드
      if (onDateSelected) {
        // 날짜 선택 콜백 호출 (업데이트 모드)
        onDateSelected(selectedSlot.id, selectedSlot);
        startCloseAnimationThen(() => {
          onClose && onClose();
        });
      } else {
        // 선택된 날짜 슬롯 ID로 장바구니에 추가
        // selectedSlot.id는 workshopDateSlotId를 의미
        // 전역 로딩 오버레이 대신 버튼 인라인 로딩으로 처리 (skipLoading)
        setIsSubmitting(true);
        await addToCart(selectedSlot.id, 1, { skipLoading: true });

        startCloseAnimationThen(() => {
          onClose && onClose();
          // 성공 알림 모달 표시
          setSuccessModalType('bag');
          setShowSuccessModal(true);
        });
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);

      // 중복 에러인 경우 모달 표시
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message || error.message;

      if (
        errorCode === 'CART_ITEM_ALREADY_EXISTS' ||
        errorMessage?.includes('이미 장바구니에 담긴') ||
        errorMessage?.includes('already exists')
      ) {
        setShowConflictModal(true);
        return;
      }

      // 기타 에러는 alert로 표시
      alert(
        errorMessage ||
          '장바구니에 추가하는데 실패했습니다. 다시 시도해주세요.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 대기열 등록도 같은 예약 행을 만든다. 나중에 자리가 나 확정될 때
   * 답변을 다시 받을 기회가 없으므로 여기서 먼저 받는다.
   */
  const handleJoinWaitlist = async () => {
    if (!selectedSlotId) return;
    if (!getSelectedSlot()) return;
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    // 이 모달은 워크숍을 안 넘겨주는 곳에서도 쓰인다. 그때는 슬롯의 워크숍을 직접 불러온다
    const target = workshop ?? (await resolveWorkshop());
    await intakeGate.run(target, proceedToJoinWaitlist);
  };

  const resolveWorkshop = async () => {
    const workshopId = getSelectedSlot()?.workshopId;
    if (!workshopId) return null;
    try {
      return await getWorkshopDetail(workshopId);
    } catch {
      // 못 불러오면 질문 없는 것으로 보고 진행한다. 필수 질문은 서버가 막는다
      return null;
    }
  };

  const proceedToJoinWaitlist = async () => {
    if (!selectedSlotId) return;

    const selectedSlot = getSelectedSlot();
    if (!selectedSlot) return;

    // 이미 다른 예약이나 대기자 명단이 있는 경우 경고 모달 표시
    if (hasExistingBooking) {
      setShowConflictModal(true);
      return;
    }

    try {
      setIsSubmitting(true);
      const addedCartItem = await addToCart(selectedSlot.id, 1, {
        skipLoading: true,
        skipCartStatusSync: true,
      });
      let cartItemId = addedCartItem?.id ?? addedCartItem?.cartItemId;

      if (!cartItemId) {
        const cartItems = await getCartItems({ skipLoading: true });
        const matched = (cartItems || []).find(
          (item) =>
            String(item?.workshopDateSlotId) === String(selectedSlot.id),
        );
        cartItemId = matched?.id;
      }

      if (!cartItemId) {
        throw new Error('Failed to resolve cart item for waitlist');
      }

      const customerName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.nickname || user?.email || '';
      const customerEmail = user?.email || user?.socialEmail || '';

      await createBookings([cartItemId], customerName, customerEmail, {
        skipLoading: true,
        answers: buildAnswersPayload([selectedSlot.workshopId]),
      });
      clearIntakeAnswers([selectedSlot.workshopId]);

      trackWorkshopBookComplete({
        workshop_id: selectedSlot.workshopId,
        workshop_name: selectedSlot.workshopTitle,
        workshop_date: selectedSlot.date,
        booking_status: 'waitlist',
        step_number: selectedSlot.stepNumber,
      });

      setSlotStatusMap((prev) => ({
        ...prev,
        [String(selectedSlot.id)]: 'onWaitlist',
      }));

      setSuccessModalType('waitlist');
      setShowSuccessModal(true);
      startCloseAnimationThen(() => {
        onClose && onClose();
      });
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      const errorCode = error?.response?.data?.code;
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message ||
        '';
      const lowerMessage = String(errorMessage).toLowerCase();

      // 이미 대기열 등록된 경우 UI 상태를 즉시 반영
      if (
        String(errorCode).includes('ALREADY') ||
        String(errorCode).includes('EXISTS') ||
        lowerMessage.includes('already') ||
        errorMessage.includes('이미')
      ) {
        setSlotStatusMap((prev) => ({
          ...prev,
          [String(selectedSlot.id)]: 'onWaitlist',
        }));
        return;
      }

      alert(errorMessage || '대기열 등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueAction = () => {
    // 중복 체크 모달에서 Continue를 눌렀을 때는 모달만 닫기
    setShowConflictModal(false);
  };

  const handleSlotClick = (slotId) => {
    const slot = displayDateSlotsWithStatus.find(
      (s) => String(s.id) === String(slotId),
    );
    if (!slot) return;

    setSelectedSlotId(
      String(slotId) === String(selectedSlotId) ? null : slotId,
    );
  };

  const renderActionButton = (isMobile = false) => {
    const buttonState = getButtonState();
    const size = isMobile ? 'l' : 'xl';
    const layoutProps = isMobile
      ? { width: '100%' }
      : { style: { flex: '1 0 0' } };

    if (!buttonState) {
      return (
        <BaseButton
          type='primary'
          size={size}
          hideIcon
          isDisabled={true}
          {...layoutProps}
        >
          Add to bag
        </BaseButton>
      );
    }

    const { type, text, disabled } = buttonState;

    if (type === 'blocked') {
      return (
        <BaseButton
          type='secondary'
          size={size}
          hideIcon
          isDisabled={true}
          bgclass='bg-grayscale-gray03'
          borderclass='border-grayscale-gray03'
          textclass='text-text-inverse'
          {...layoutProps}
        >
          {text}
        </BaseButton>
      );
    }

    if (
      type === 'inBag' ||
      type === 'booked' ||
      type === 'ongoing' ||
      type === 'closed' ||
      type === 'completed'
    ) {
      return (
        <BaseButton
          type='primary'
          size={size}
          hideIcon
          isDisabled={true}
          bgclass='bg-grayscale-gray03'
          borderclass='border-grayscale-gray03'
          textclass='text-text-inverse'
          {...layoutProps}
        >
          {text}
        </BaseButton>
      );
    }

    if (type === 'onWaitlist') {
      return (
        <BaseButton
          type='secondary'
          size={size}
          hideIcon
          isDisabled={true}
          bgclass='bg-grayscale-gray00'
          borderclass='border-grayscale-gray03'
          textclass='text-grayscale-gray03'
          {...layoutProps}
        >
          {text}
        </BaseButton>
      );
    }

    if (type === 'joinWaitlist') {
      return (
        <BaseButton
          type='secondary'
          size={size}
          hideIcon
          onClick={handleJoinWaitlist}
          isDisabled={disabled || isSubmitting}
          isLoading={isSubmitting}
          {...layoutProps}
        >
          {text}
        </BaseButton>
      );
    }

    return (
      <BaseButton
        type='primary'
        size={size}
        hideIcon
        onClick={handleAddToBag}
        isDisabled={disabled || isSubmitting}
        isLoading={isSubmitting}
        {...layoutProps}
      >
        {text}
      </BaseButton>
    );
  };

  const shouldRenderModal = isOpen || isClosing;

  const overlayClasses = classNames(
    'fixed inset-0 flex justify-center z-[1000] transition-opacity duration-300 ease-out',
    'md:items-center',
    'items-end',
    {
      'opacity-0': isClosing,
      'opacity-100': !isClosing,
      'md:bg-surface-black40 md:opacity-100': !isClosing,
      'md:bg-transparent': isClosing,
      'bg-surface-black40': true,
    },
  );

  const containerClasses = classNames(
    'relative bg-grayscale-gray00 shadow-md flex flex-col overflow-hidden',
    'min-w-full max-h-[90vh] px-[40px] pt-[16px] pb-[24px] rounded-t-[40px] softcorners-70-top',
    'md:bg-grayscale-gray01 md:p-0 md:w-full md:min-w-[660px] md:max-w-[660px] md:rounded-[30px] md:softcorners-70 md:flex md:flex-col md:items-center md:overflow-clip',
    {
      'animate-slideDown': isClosing,
      'animate-slideUp': !isClosing && !isDragging,
      'md:animate-fadeIn md:will-change-auto': !isClosing && !isDragging,
      'md:animate-none': isClosing,
    },
  );

  const handleBarClasses =
    'block w-[46px] h-[6px] bg-grayscale-gray02 rounded-2xl mx-[auto] mb-[16px] md:hidden';

  return (
    <>
      <IntakeQuestionsModal {...intakeGate.modalProps} />
      {shouldRenderModal &&
        createPortal(
          <div className={overlayClasses} onClick={handleOverlayClick}>
            <div
              ref={modalRef}
              className={containerClasses}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                transform:
                  isDragging || isClosing
                    ? `translateY(${translateY}px)`
                    : 'translateY(0)',
                transition: isDragging ? 'none' : 'transform 0.3s ease-out',
              }}
            >
              <div className={handleBarClasses} />

              {/* Header */}
              <div className='flex items-center justify-between pb-[24px] pt-0 px-0 shrink-0 w-full md:justify-center md:pb-[18px] md:pt-[24px]'>
                <p
                  className={`${applyTypography(
                    'title3',
                  )} text-text-primary md:hidden`}
                >
                  Choose your date
                </p>
                <p
                  className={`${applyTypography(
                    'b16Strong',
                  )} hidden text-text-primary text-center shrink-0 w-full md:block`}
                  style={{ lineHeight: '1.5' }}
                >
                  Choose your date
                </p>
                <BaseButton
                  type='text'
                  size='s'
                  htmlType='button'
                  onClick={handleCancel}
                  className='bg-grayscale-gray01 hover:bg-grayscale-gray01 h-auto min-w-0 border-0 p-[8px] rounded-[8px] shrink-0 md:hidden'
                  aria-label='Close modal'
                  icon={<CancelIcon className='w-4 h-4 text-icon-primary' />}
                />
              </div>

              {/* Content */}
              <div className='flex flex-col items-start min-h-0 flex-1 shrink-0 w-full md:px-[40px] md:pb-[30px]'>
                {/* Date Slots Grid */}
                <div className='flex flex-col gap-[8px] items-start pb-[24px] pt-0 px-0 shrink-0 w-full overflow-y-auto md:gap-[12px] md:overflow-visible'>
                  <div className='flex flex-wrap gap-[8px] items-center justify-start shrink-0 w-full md:gap-[12px]'>
                    {displayDateSlotsWithStatus.map((slot, idx) => (
                      <DateSlotCard
                        key={slot.id ?? idx}
                        slot={slot}
                        isSelected={String(selectedSlotId) === String(slot.id)}
                        onClick={handleSlotClick}
                        variant='modal'
                      />
                    ))}
                  </div>
                </div>

                {/* Action Buttons (Desktop) */}
                <div className='hidden gap-[12px] items-start shrink-0 w-full md:flex'>
                  <BaseButton
                    type='text'
                    size='xl'
                    hideIcon
                    onClick={handleCancel}
                    bgclass='bg-grayscale-gray01'
                    borderclass='border-grayscale-gray01'
                  >
                    cancel
                  </BaseButton>
                  {renderActionButton(false)}
                </div>

                {/* Action Buttons (Mobile) */}
                <div className='flex flex-col gap-[12px] items-start shrink-0 w-full md:hidden'>
                  {renderActionButton(true)}
                  <BaseButton
                    type='text'
                    size='s'
                    htmlType='button'
                    hideIcon
                    typo={false}
                    isStatic
                    onClick={handleCancel}
                    textWrapperClass='h-auto'
                    className={`${applyTypography(
                      'b16',
                    )} min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 text-text-secondary text-center w-full h-[26px]`}
                  >
                    cancel
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Booking Conflict Modal */}
      <BookingConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onContinue={handleContinueAction}
      />

      {/* Success Notification Toast */}
      <SuccessNotificationToast
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        type={successModalType}
      />

      {/* Login Required Modal */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onView={() => {
          trackCtaClick('signup_modal_view', {
            trigger_source: 'kpathport_date_selection_cta',
            page_path: window.location.pathname,
          });
        }}
        onMethodSelect={(method) => {
          trackCtaClick('signup_method_select', {
            method,
            trigger_source: 'kpathport_date_selection_cta',
          });
        }}
      />
    </>
  );
};

export default DateSelectionModal;
